import React, { memo } from 'react';
import type { LucideIcon } from 'lucide-react';

/**
 * ProgressCounter - Displays a count with an icon (e.g., stars earned, streak count)
 * 
 * @example
 * <ProgressCounter icon={Star} current={5} total={10} variant="gold" />
 * <ProgressCounter icon={Flame} current={3} variant="orange" label="רצף" />
 */

export type ProgressVariant = 'gold' | 'green' | 'blue' | 'purple' | 'orange';

export interface ProgressCounterProps {
  /** Lucide icon component */
  icon: LucideIcon;
  /** Current count value */
  current: number;
  /** Total count (optional - if provided, shows as current/total) */
  total?: number;
  /** Color variant */
  variant?: ProgressVariant;
  /** Optional label text */
  label?: string;
  /** Additional CSS classes */
  className?: string;
}

const variantStyles: Record<ProgressVariant, { container: string; icon: string; text: string }> = {
  gold: {
    container: 'bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-700',
    icon: 'text-yellow-500',
    text: 'text-yellow-700 dark:text-yellow-400',
  },
  green: {
    container: 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-700',
    icon: 'text-green-500',
    text: 'text-green-700 dark:text-green-400',
  },
  blue: {
    container: 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-700',
    icon: 'text-blue-500',
    text: 'text-blue-700 dark:text-blue-400',
  },
  purple: {
    container: 'bg-purple-50 border-purple-200 dark:bg-purple-900/20 dark:border-purple-700',
    icon: 'text-purple-500',
    text: 'text-purple-700 dark:text-purple-400',
  },
  orange: {
    container: 'bg-orange-50 border-orange-200 dark:bg-orange-900/20 dark:border-orange-700',
    icon: 'text-orange-500',
    text: 'text-orange-700 dark:text-orange-400',
  },
};

export const ProgressCounter: React.FC<ProgressCounterProps> = memo(({
  icon: Icon,
  current,
  total,
  variant = 'gold',
  label,
  className = '',
}) => {
  const styles = variantStyles[variant];
  
  return (
    <div
      className={`
        glass rounded-full px-3 py-1.5 
        shadow-md border
        flex items-center gap-2
        ${styles.container}
        ${className}
      `}
    >
      <Icon className={styles.icon} size={16} />
      <span className={`text-sm font-bold font-heebo ${styles.text}`}>
        {total !== undefined ? `${current}/${total}` : current}
        {label && ` ${label}`}
      </span>
    </div>
  );
});

ProgressCounter.displayName = 'ProgressCounter';
